/* @HEADER
 * @version 1.0.2418 | 2026-09-29
 * @purpose Answers what the person must be told and asked before a Space is deleted — how many boards it holds, and which Spaces may receive them.
 * @sidecar spaceDeleteRequest.mjs.header.md
 * @layer app | @hex adapter | @ctx kanban
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-7110, ZVX-DEV-5637, ZVX-DEV-5636, ZVX-DEV-3615, ZVX-DEV-5213

import {
  isAllProjectsSpace,
  isAllTasksSpace,
  isOrgTasksSpace,
  isUndeletableSpace,
} from '../../../../modules/board/public-api.mjs'

/**
 * ## Why this exists
 *
 * Deleting a Space asked nothing. The operator made «Тест», put three boards in
 * it, deleted it, and found `C`, `D`, `E` in «Входящие» — no question, no
 * message, and the boards' recorded desire still pointing at the Space that no
 * longer existed.
 *
 * The house already knows the shape. Deleting a BOARD asks where its tasks go
 * (`BoardSettingsPanel`, `{ mode: 'move-tasks', targetBoardId }`), and deleting
 * a task with subtasks asks detach-or-cascade (`cascade-delete-workflow`). The
 * rule both follow — **when the target has dependents, the decision goes to the
 * person** — simply never reached the Space.
 *
 * ZVX-DEV-5636 gave the deletion a destination it can be told. This says what
 * to tell the person so they can name one.
 *
 * ## Which Spaces may receive the boards
 *
 * The operator's recorded decision on the neighbouring move-a-board workflow is
 * «показывать только видимые» — and it is not cosmetic there: moving a board
 * into a Space nobody can navigate to reproduces the very accident that made
 * the feature necessary. The same applies here, with one measured correction.
 *
 * The AGGREGATES are excluded: «Все задачи», «Все проекты», «Все задачи
 * организации» are views over boards, not places boards live.
 *
 * «Входящие» — the built-in catch-all — is NOT excluded. A comment in
 * `boardCatalogSpacePlacement` says a board moved there «is GONE, with no route
 * in the interface leading to it», but that is not what the operator's screen
 * shows: measured 2026-09-07, his «Входящие» holds 11 boards and he can open
 * every one. Hiding it from this list would remove a destination he uses, on
 * the strength of a comment that his own machine contradicts.
 */

/**
 * System spaces that are views, not places — a board cannot live in one.
 *
 * ZVX-DEV-7110: this asks the board module's own predicates rather than keeping
 * a second copy of the knowledge. The copy it replaces matched on `system_kind`
 * alone, while `isUndeletableSpace` two lines below answers on **the id OR the
 * marker** — so an aggregate row that reached the database without its marker
 * was refused deletion and offered as a destination at the same time. Asking the
 * same predicates makes the two halves of this function unable to disagree.
 */
function isAggregate(group) {
  return isAllTasksSpace(group) || isAllProjectsSpace(group) || isOrgTasksSpace(group)
}

function isTombstoned(group) {
  return group?.pendingDelete === true
}

/**
 * @param {string} groupId the Space the person asked to delete
 * @param {readonly object[]} groups every Space of this project
 * @param {readonly object[]} boards every board of this project
 * @returns {{
 *   space: object | null,
 *   refused: boolean,
 *   boardCount: number,
 *   needsChoice: boolean,
 *   destinations: object[],
 * }}
 */
export function planSpaceDeleteRequest(groupId, groups, boards) {
  const all = Array.isArray(groups) ? groups : []
  const space = all.find((g) => g && g.id === groupId) ?? null

  // ZVX-DEV-3615 — one predicate the UI and the data layer both ask, so a
  // dialog is never opened for something the rule will refuse anyway.
  if (!space || isUndeletableSpace(space)) {
    return { space, refused: true, boardCount: 0, needsChoice: false, destinations: [] }
  }

  const held = (Array.isArray(boards) ? boards : []).filter((b) => b && b.group_id === groupId)

  const destinations = all
    .filter((g) => g && g.id !== groupId && !isAggregate(g) && !isTombstoned(g))
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))

  return {
    space,
    refused: false,
    boardCount: held.length,
    // A Space holding nothing needs a confirmation and no question: there is
    // nothing to decide, and offering a destination for zero boards is noise.
    needsChoice: held.length > 0 && destinations.length > 0,
    destinations,
  }
}
